import { useMemo, useState } from "react";
import { api, type HelpChatTurn } from "../api";
import type { NavKey } from "./Sidebar";

// Layer 1 of in-app help: curated, hand-written answers to the questions users
// actually hit — accurate by construction (no AI here, so nothing is invented).
// Grouped by topic and filterable. A later layer can add a grounded AI chat that
// seeds from these same questions; this panel is the trustworthy baseline.

interface Faq {
  q: string;
  /** Plain-text answer (kept as text so it stays easy to audit for accuracy). */
  a: string;
  /** Optional in-app jump the answer offers (closes the panel and navigates). */
  action?: { label: string; nav: NavKey };
  /** Extra keywords to match in search beyond the question/answer text. */
  keywords?: string;
}

const FAQ: { category: string; items: Faq[] }[] = [
  {
    category: "Getting started",
    items: [
      {
        q: "How do I connect a social account?",
        a: "Open Connections, click Connect next to the platform, then sign in and approve access. Each workspace holds one account per platform.",
        action: { label: "Go to Connections", nav: "connections" },
        keywords: "link add oauth authorize sign in",
      },
      {
        q: "What is a workspace (brand)?",
        a: "A workspace is a separate brand with its own connected accounts, calendar, drafts, and analytics. Switch brands with the picker at the bottom-left; switching always returns you to the Dashboard so you know where you are.",
        keywords: "brand multiple accounts switch",
      },
      {
        q: "What platforms are supported?",
        a: "YouTube, TikTok, Instagram, Facebook, and X. Connect any combination and publish to them together from one post.",
        keywords: "youtube tiktok instagram facebook x twitter",
      },
    ],
  },
  {
    category: "Publishing",
    items: [
      {
        q: "How do I publish a post?",
        a: "Create post → write your caption (YouTube also needs a title), attach a video or image, pick the platforms, then Publish to send now, or set a time to schedule it.",
        action: { label: "Create a post", nav: "compose" },
        keywords: "post now send dispatch",
      },
      {
        q: "Does Broadside tailor the post per platform?",
        a: "Yes — you write once and Broadside adapts the hook, caption, and title for each platform. You can review and edit each platform's version in the composer before sending.",
        keywords: "ai caption tailor per platform edit",
      },
      {
        q: "If one platform fails, do the others still go out?",
        a: "Yes. Each platform publishes independently — one leg failing never blocks the rest. The result of every platform is shown when publishing finishes, and anything that failed is saved in your Activity log.",
        action: { label: "Open Activity", nav: "activity" },
        keywords: "fail error partial independent",
      },
    ],
  },
  {
    category: "Scheduling & timezone",
    items: [
      {
        q: "How does scheduling work?",
        a: "Pick a future date and time when creating a post and it publishes automatically then. Scheduled posts appear on your Planner/Calendar, where you can edit or cancel them any time before they fire.",
        action: { label: "Open Planner", nav: "planner" },
        keywords: "schedule later calendar time",
      },
      {
        q: "What timezone are my times in?",
        a: "Your browser's timezone by default. You can pick a specific zone in Settings → Timezone; after that, every time you enter and every time shown uses that zone, and a post scheduled for 6:00 PM publishes at 6:00 PM in it.",
        action: { label: "Open Settings", nav: "settings" },
        keywords: "time zone tz utc clock",
      },
      {
        q: "Can I upload a whole folder of videos at once?",
        a: "Yes — import a folder in Create post and Broadside spaces the videos across a posting cadence you choose. Each one becomes its own card where you can adjust the time, caption, and platforms before scheduling.",
        action: { label: "Create a post", nav: "compose" },
        keywords: "batch bulk folder multiple videos cadence",
      },
    ],
  },
  {
    category: "Accounts & troubleshooting",
    items: [
      {
        q: 'A post failed with "session invalidated" or "reconnect needed" — why?',
        a: "That account's connection expired or was invalidated — most often because its password was changed, which revokes the saved access. Reconnect the account in Connections and it'll work again. Other platforms are unaffected.",
        action: { label: "Go to Connections", nav: "connections" },
        keywords: "token expired revoked password reconnect instagram facebook",
      },
      {
        q: "Can the same social account be connected in two workspaces?",
        a: "It's allowed, but publishing the same account from two workspaces can double-post and share that platform's rate limits, so Broadside shows a warning when it detects it. Keep an account in one workspace unless you specifically need it in more.",
        keywords: "duplicate two brands double post",
      },
    ],
  },
  {
    category: "Platforms",
    items: [
      {
        q: "How do I post to TikTok?",
        a: "TikTok is fully enabled — its Content Posting audit is approved, so Broadside can post publicly to TikTok. Connect a TikTok account on the Connections page, then in the composer set the post's privacy level and interaction settings (comment/duet/stitch) yourself — TikTok requires those to be chosen manually, so they're never pre-filled.",
        keywords: "tiktok public private privacy audit approved connect post",
      },
      {
        q: "What kind of Instagram account do I need?",
        a: "A Professional Instagram account (Business or Creator — personal accounts can't publish via the API). Connect it through Instagram's own login. Video is published as a Reel.",
        keywords: "instagram business creator professional reel",
      },
    ],
  },
  {
    category: "Billing & credits",
    items: [
      {
        q: "What are credits and how are they spent?",
        a: "Each publish spends credits, weighted by platform (some platforms cost more than others). Your plan includes a monthly allotment; you can top up or change plan any time. Current amounts and your balance are in Settings → Billing.",
        action: { label: "Open Billing", nav: "settings" },
        keywords: "credit cost plan upgrade topup price",
      },
      {
        q: "What do the analytics show?",
        a: "Engagement — views, likes, and comments — for the posts you published through Broadside, so you can see how each one performed across platforms.",
        action: { label: "Open Analytics", nav: "analytics" },
        keywords: "analytics metrics views likes performance",
      },
    ],
  },
];

/** Seed questions shown in the chat's empty state — one tap sends them to the AI. */
const CHAT_SUGGESTIONS = [
  "How do credits work?",
  "Why did my Instagram post fail?",
  "How do I schedule in a different timezone?",
  "Why can't I post to TikTok publicly?",
];

export function HelpPanel({ onNavigate }: { onNavigate?: (key: NavKey) => void }) {
  const [query, setQuery] = useState("");

  // "Ask Broadside" — the grounded AI assistant (Layer 2). The curated FAQ below
  // is always available; the chat handles freeform questions.
  const [messages, setMessages] = useState<HelpChatTurn[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [aiOff, setAiOff] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);

  async function ask(question: string) {
    const q = question.trim();
    if (!q || busy) return;
    setChatError(null);
    const priorHistory = messages;
    setMessages((m) => [...m, { role: "user", content: q }]);
    setInput("");
    setBusy(true);
    try {
      const res = await api.help(q, priorHistory);
      if (!res.configured) {
        setAiOff(true);
        setMessages((m) => [
          ...m,
          {
            role: "assistant",
            content:
              "AI help isn't available right now — browse the FAQ below, or email support@broadside.social.",
          },
        ]);
        return;
      }
      setMessages((m) => [...m, { role: "assistant", content: res.answer ?? "" }]);
    } catch (err) {
      setChatError(err instanceof Error ? err.message : "Couldn't get an answer just now.");
    } finally {
      setBusy(false);
    }
  }

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return FAQ;
    return FAQ.map((g) => ({
      category: g.category,
      items: g.items.filter((it) =>
        `${it.q} ${it.a} ${it.keywords ?? ""}`.toLowerCase().includes(q),
      ),
    })).filter((g) => g.items.length > 0);
  }, [query]);

  return (
    <div className="help">
      {/* Layer 2 — grounded AI assistant. */}
      <section className="help-chat">
        <h3 className="help-cat">Ask Broadside</h3>
        <div className="help-chat-thread">
          {messages.length === 0 && (
            <div className="help-chat-empty muted small">
              Ask anything about using Broadside — I answer from the help docs. Try:
              <div className="help-chips">
                {CHAT_SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className="help-chip"
                    onClick={() => void ask(s)}
                    disabled={busy}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`help-msg help-msg--${m.role}`}>
              {m.content}
            </div>
          ))}
          {busy && (
            <div className="help-msg help-msg--assistant help-msg--typing" aria-live="polite">
              Thinking…
            </div>
          )}
        </div>
        {chatError && <p className="error small">{chatError}</p>}
        {!aiOff && (
          <form
            className="help-chat-input"
            onSubmit={(e) => {
              e.preventDefault();
              void ask(input);
            }}
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask a question…"
              disabled={busy}
              aria-label="Ask the help assistant"
            />
            <button type="submit" className="btn primary small" disabled={busy || !input.trim()}>
              Ask
            </button>
          </form>
        )}
        <p className="help-chat-note muted small">
          Answers come from Broadside's help docs — double-check anything important.
        </p>
      </section>

      <div className="help-divider" role="separator" />

      <h3 className="help-cat">Browse common questions</h3>
      <input
        type="search"
        className="help-search"
        placeholder="Search help…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        aria-label="Search help"
      />

      {groups.length === 0 && (
        <p className="muted small help-empty">
          No matches. Try different words, or email{" "}
          <a href="mailto:support@broadside.social">support@broadside.social</a>.
        </p>
      )}

      {groups.map((g) => (
        <section key={g.category} className="help-group">
          <h3 className="help-cat">{g.category}</h3>
          {g.items.map((it) => (
            <details key={it.q} className="help-item">
              <summary>{it.q}</summary>
              <div className="help-answer">
                <p>{it.a}</p>
                {it.action && onNavigate && (
                  <button
                    type="button"
                    className="btn ghost small"
                    onClick={() => onNavigate(it.action!.nav)}
                  >
                    {it.action.label} →
                  </button>
                )}
              </div>
            </details>
          ))}
        </section>
      ))}

      <p className="help-foot muted small">
        Still stuck? Email <a href="mailto:support@broadside.social">support@broadside.social</a>{" "}
        and we'll help.
      </p>
    </div>
  );
}
